import {
	useState,
	useMemo,
	useEffect,
	useRef,
	type ComponentType,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	ArrowRight,
	ArrowRightCircle,
	CheckCircle2,
	ChevronLeft,
	ClipboardList,
	Clock,
	ShieldCheck,
	UserRound,
} from 'lucide-react';
import {
	DynamicIcon as DynamicIconRaw,
	type IconName,
} from 'lucide-react/dynamic';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';
import { maskCPF } from '@/utils/masks';
import { validateCPF } from '@/utils/validations';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/api/api';
import { FormService } from '@/services/form.service';
import { useLoginEmployee } from '@/services/mutations/employee';
import { useSubmitFormAnswer } from '@/services/mutations/form';
import { NotFound } from '@/pages/public/NotFound';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	resolveCompanyPortalProfile,
	type CompanyPortalProfileInput,
} from '@/utils/companyPortal';
import {
	companyFormularioPath,
	parseCompanyFormularioCompanyId,
} from './companyFormularioPaths';
import {
	getContrastTextClassForColorInput,
	hexPattern,
	normalizeHex,
	resolveColorInputToHex,
} from '@/utils/formQuestionColor';
import { filterEmployeeAvailableForms } from '@/utils/availableForms';
import { buildFormAnswerPayload } from '@/utils/formAnswerPayload';
import {
	countAnsweredKeys,
	getActiveQuestions,
	pruneInactiveQuestionAnswers,
} from '@/utils/formQuestionShowCondition';
import { toast } from 'react-toastify';
import { EmployeeService } from '@/services/employee.service';
import {
	clearCompanyFormularioDraft,
	loadCompanyFormularioDraft,
	saveCompanyFormularioDraft,
} from './companyFormularioDraft';
import type { FlowStep } from './companyFormularioTypes';

const DynamicIcon = DynamicIconRaw as ComponentType<{
	name: IconName;
	size?: number | string;
	className?: string;
}>;

const hexToRgba = (hex: string, alpha: number) => {
	if (!hexPattern.test(hex)) return undefined;
	const n = normalizeHex(hex).replace('#', '');
	const r = parseInt(n.slice(0, 2), 16);
	const g = parseInt(n.slice(2, 4), 16);
	const b = parseInt(n.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const cpfSchema = z.object({
	cpf: z
		.string()
		.min(1, 'CPF é obrigatório')
		.refine((v) => validateCPF(v), 'CPF inválido'),
});
type CpfFormData = z.infer<typeof cpfSchema>;

function portalCompanyFromEmployeeMe(
	me: Employees.EmployeeMe,
): CompanyPortalProfileInput {
	return {
		trade_name: me.company.trade_name,
		banner_img_url: me.company.banner_img_url ?? null,
		logo_img_url: me.company.logo_img_url ?? null,
	};
}

const BETWEEN_FORMS_RING_R = 42;
const BETWEEN_FORMS_RING_C = 2 * Math.PI * BETWEEN_FORMS_RING_R;

/** Anel de progresso (traço parcial, ex. 1/2 = metade do círculo). */
function BetweenFormsProgressRing({
	completed,
	total,
}: {
	completed: number;
	total: number;
}) {
	const ratio = total > 0 ? Math.min(1, Math.max(0, completed / total)) : 0;
	const filled = BETWEEN_FORMS_RING_C * ratio;
	return (
		<svg
			viewBox="0 0 100 100"
			className="h-28 w-28 -rotate-90"
			role="img"
			aria-label={`${completed} de ${total} formulários`}
		>
			<circle
				cx="50"
				cy="50"
				r={BETWEEN_FORMS_RING_R}
				fill="none"
				className="stroke-zinc-200"
				strokeWidth="8"
			/>
			<circle
				cx="50"
				cy="50"
				r={BETWEEN_FORMS_RING_R}
				fill="none"
				className="stroke-brand-orange"
				strokeWidth="8"
				strokeLinecap="round"
				strokeDasharray={`${filled} ${BETWEEN_FORMS_RING_C}`}
			/>
		</svg>
	);
}

/** Banner após login (`GET /employees/me` → `company`). Antes do CPF, vazio. */
function CompanyFormularioHeaderCompanyBanner({
	company,
}: {
	company: CompanyPortalProfileInput | null;
}) {
	const [imgError, setImgError] = useState(false);
	const { displayName, headerTitle, bannerUrl } =
		resolveCompanyPortalProfile(company);

	useEffect(() => {
		setImgError(false);
	}, [company?.banner_img_url, company?.logo_img_url, company?.trade_name]);
	const imgUrl = imgError ? null : bannerUrl;
	const bannerSlotClass =
		'relative h-11 max-w-[min(42vw,20rem)] min-w-[8rem] shrink-0 overflow-hidden rounded-md border border-stone-300 sm:max-w-md';

	if (imgUrl) {
		return (
			<div className={`group ${bannerSlotClass}`}>
				<img
					src={imgUrl}
					alt={displayName || 'Banner da empresa'}
					className="h-full w-full object-cover object-center"
					onError={() => setImgError(true)}
				/>
				{displayName ? (
					<div className="absolute inset-x-0 bottom-0 flex h-5 translate-y-6 items-center justify-center bg-black/55 opacity-0 transition-all duration-200 ease-in-out group-hover:translate-y-0 group-hover:opacity-100">
						<span className="truncate px-2 text-[10px] font-semibold uppercase leading-none text-white">
							{displayName}
						</span>
					</div>
				) : null}
			</div>
		);
	}

	if (headerTitle) {
		return (
			<div
				className={`${bannerSlotClass} flex items-center justify-center bg-stone-100 px-3`}
			>
				<span className="truncate text-xs font-semibold uppercase text-stone-800">
					{headerTitle}
				</span>
			</div>
		);
	}

	return null;
}

function CompanyFormularioContent({ companyId }: { companyId: string }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [sessionBoot, setSessionBoot] = useState<'loading' | 'ready'>(
		'loading',
	);
	const [flowStep, setFlowStep] = useState<FlowStep>('cpf');
	const [allForms, setAllForms] = useState<Forms.FormWithQuestions[]>([]);
	const [currentFormIndex, setCurrentFormIndex] = useState(0);
	const [currentQuestionStep, setCurrentQuestionStep] = useState(0);
	const [answers, setAnswers] = useState<Record<string, string | null>>({});
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);
	const [isCpfStepLoading, setIsCpfStepLoading] = useState(false);
	const [completedFormTitle, setCompletedFormTitle] = useState<string | null>(
		null,
	);
	const [portalCompany, setPortalCompany] =
		useState<CompanyPortalProfileInput | null>(null);
	/** Intro com lista de formulários só na primeira vez; 2º+ form vai direto às perguntas. */
	const [introDismissed, setIntroDismissed] = useState(false);

	const { mutateAsync: loginEmployee, isPending: isLoginEmployeePending } =
		useLoginEmployee();
	const { mutateAsync: submitAnswer, isPending: isSubmitting } =
		useSubmitFormAnswer();

	const nextButtonRef = useRef<HTMLDivElement>(null);
	const prevCanAdvanceRef = useRef(false);
	const stageRootRef = useRef<HTMLDivElement>(null);
	const betweenFormsIconRef = useRef<HTMLDivElement>(null);
	const [totalFormsInSession, setTotalFormsInSession] = useState(0);
	const [betweenFormsCountdown, setBetweenFormsCountdown] = useState(5);
	const submitInFlightRef = useRef(false);
	const [isSubmitLocked, setIsSubmitLocked] = useState(false);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset: resetCpfForm,
		formState: { errors },
	} = useForm<CpfFormData>({ resolver: zodResolver(cpfSchema) });

	const resetSessionToCpf = async (_options?: { silent?: boolean }) => {
		try {
			await api.delete('/auth/logout');
		} catch {
			/* cookie já ausente ou sessão expirada */
		}
		clearCompanyFormularioDraft(companyId);
		await queryClient.removeQueries({
			queryKey: ['forms', 'available', 'employee', 'me'],
		});
		setAllForms([]);
		setCurrentFormIndex(0);
		setCurrentQuestionStep(0);
		setAnswers({});
		setCompletedFormTitle(null);
		setPortalCompany(null);
		setIntroDismissed(false);
		setTotalFormsInSession(0);
		resetCpfForm({ cpf: '' });
		setFlowStep('cpf');
	};

	const redirectToEmployeeCompanyUrl = (employeeCompanyId: string) => {
		if (employeeCompanyId === companyId) return;
		navigate(companyFormularioPath(employeeCompanyId), { replace: true });
	};

	const sortedForms = useMemo(
		() => filterEmployeeAvailableForms(allForms),
		[allForms],
	);

	const currentForm = sortedForms[currentFormIndex] ?? null;

	const allFormQuestions = useMemo(
		() =>
			[...(currentForm?.form_question ?? [])].sort(
				(a, b) => a.number - b.number,
			),
		[currentForm?.form_question],
	);

	const activeQuestions = useMemo(
		() => getActiveQuestions(allFormQuestions, answers),
		[allFormQuestions, answers],
	);

	const totalSteps = activeQuestions.length;
	const currentQuestion = activeQuestions[currentQuestionStep] ?? null;

	const rawQuestionColor =
		currentQuestion?.color && currentQuestion.color.trim().length > 0
			? currentQuestion.color.trim()
			: '#e5e7eb';
	const resolvedQuestionColor =
		resolveColorInputToHex(rawQuestionColor) ?? '#e5e7eb';
	const PROGRESS_COLOR_CLASS = 'bg-teal-500';
	const questionNumber = currentQuestion?.number ?? 1;
	const questionTextColorClass = getContrastTextClassForColorInput(
		rawQuestionColor,
		'#e5e7eb',
	);

	const progressPercent = useMemo(
		() =>
			totalSteps
				? Math.round(((currentQuestionStep + 1) / totalSteps) * 100)
				: 0,
		[currentQuestionStep, totalSteps],
	);

	const totalQuestionsAllForms = useMemo(
		() =>
			sortedForms.reduce(
				(acc, f) =>
					acc +
					getActiveQuestions(f.form_question ?? [], answers).length,
				0,
			),
		[sortedForms, answers],
	);
	const totalSecondsEstimate = totalQuestionsAllForms * 15;
	const estimatedTimeLabel =
		totalSecondsEstimate < 60
			? `${totalSecondsEstimate} s`
			: `${Math.floor(totalSecondsEstimate / 60)} min${
					totalSecondsEstimate % 60 > 0
						? ` ${totalSecondsEstimate % 60}s`
						: ''
			  }`;

	// --- helpers ---

	const findScrollParent = (startEl: HTMLElement | null) => {
		if (typeof window === 'undefined') return null;
		let node: HTMLElement | null = startEl?.parentElement ?? null;
		while (node && node !== document.body) {
			const style = window.getComputedStyle(node);
			const overflowY = style.overflowY;
			if (
				(overflowY === 'auto' || overflowY === 'scroll') &&
				node.scrollHeight > node.clientHeight
			)
				return node;
			node = node.parentElement;
		}
		return (document.scrollingElement as HTMLElement | null) ?? null;
	};

	const scrollToTop = (behavior: ScrollBehavior = 'smooth') => {
		if (typeof window === 'undefined') return;
		const target = findScrollParent(stageRootRef.current);
		target?.scrollTo({ top: 0, behavior });
		window.scrollTo({ top: 0, behavior });
	};

	const setAnswer = (key: string, value: string | null) => {
		setAnswers((prev) => {
			const merged = { ...prev, [key]: value };
			return pruneInactiveQuestionAnswers(allFormQuestions, merged);
		});
	};

	const canAdvance = useMemo(() => {
		if (!currentQuestion) return false;
		const v = answers[String(currentQuestion.number)];
		if (currentQuestion.type === 'FREE_TEXT')
			return (v ?? '').trim().length > 0;
		if (currentQuestion.type === 'SINGLE_CHOICE')
			return (v ?? '').length > 0;
		if (currentQuestion.type === 'MULTIPLE_CHOICE') {
			const validKeys = new Set(
				(currentQuestion.options ?? []).map((o) => o.key),
			);
			const chars = (v ?? '').split('').filter((c) => c.trim() !== '');
			return chars.length > 0 && chars.every((c) => validKeys.has(c));
		}
		return false;
	}, [currentQuestion, answers]);

	// --- handlers ---

	const onSubmitCpf = async (data: CpfFormData) => {
		const cpf = data.cpf.replace(/\D/g, '');
		setIsCpfStepLoading(true);
		try {
			try {
				await api.delete('/auth/logout');
			} catch {
				/* troca de CPF: garante que só a última sessão employee vale */
			}
			await loginEmployee({ companyId, cpf });
			const me = await EmployeeService.getMe();
			if (me.company_id !== companyId) {
				redirectToEmployeeCompanyUrl(me.company_id);
				return;
			}
			setPortalCompany(portalCompanyFromEmployeeMe(me));

			clearCompanyFormularioDraft(companyId);
			const res = await FormService.getAvailableForEmployee({
				page: 1,
				limit: 100,
			});
			const available = filterEmployeeAvailableForms(res.data ?? []);
			if (available.length === 0) {
				setFlowStep('no-form');
			} else {
				setAllForms(available);
				setTotalFormsInSession(available.length);
				setCurrentFormIndex(0);
				setCurrentQuestionStep(0);
				setAnswers({});
				setIntroDismissed(false);
				setFlowStep('intro');
			}
		} catch (error) {
			const status = axios.isAxiosError(error)
				? error.response?.status
				: undefined;
			if (status === 429) {
				toast.error(
					'Muitas tentativas de login. Aguarde cerca de 1 minuto e tente novamente.',
				);
			} else if (status === 404) {
				toast.error(
					'CPF não encontrado para esta empresa. Verifique os dados ou fale com o RH.',
				);
			} else {
				toast.error(
					getErrorMessage(
						error,
						'Não foi possível validar o CPF. Tente novamente.',
					),
				);
			}
		} finally {
			setIsCpfStepLoading(false);
		}
	};

	const handleStartForm = () => {
		setIntroDismissed(true);
		setFlowStep('form');
	};

	const handleSingleChoice = (key: string) => {
		if (!currentQuestion) return;
		setAnswer(String(currentQuestion.number), key);
	};

	const handleMultipleToggle = (key: string) => {
		if (!currentQuestion) return;
		const current = (answers[String(currentQuestion.number)] ?? '')
			.split('')
			.map((s) => s.trim())
			.filter(Boolean);
		const next = current.includes(key)
			? current.filter((k) => k !== key)
			: [...current, key];
		setAnswer(String(currentQuestion.number), next.sort().join(''));
	};

	const handleNext = async () => {
		if (!currentQuestion || submitInFlightRef.current || isSubmitting)
			return;

		if (currentQuestionStep < totalSteps - 1) {
			setCurrentQuestionStep((s) => s + 1);
			return;
		}

		if (!currentForm?.id) return;

		const answerPayload = buildFormAnswerPayload(allFormQuestions, answers);
		const applicationPeriodId =
			currentForm.application_mode === 'PER_APPLICATION_PERIOD'
				? currentForm.active_application_period_id ?? null
				: null;

		if (
			currentForm.application_mode === 'PER_APPLICATION_PERIOD' &&
			!applicationPeriodId
		) {
			toast.error(
				'Período de aplicação indisponível. Atualize a página ou fale com o RH.',
			);
			try {
				const res = await FormService.getAvailableForEmployee({
					page: 1,
					limit: 100,
				});
				setAllForms(filterEmployeeAvailableForms(res.data ?? []));
			} catch {
				/* lista atualizada na próxima tentativa */
			}
			return;
		}

		submitInFlightRef.current = true;
		setIsSubmitLocked(true);
		try {
			await submitAnswer({
				formId: currentForm.id,
				body: {
					answer: answerPayload,
					...(applicationPeriodId
						? { application_period_id: applicationPeriodId }
						: {}),
				},
			});

			const res = await FormService.getAvailableForEmployee({
				page: 1,
				limit: 100,
			});
			const remaining = filterEmployeeAvailableForms(res.data ?? []);
			setAllForms(remaining);

			if (remaining.length === 0) {
				clearCompanyFormularioDraft(companyId);
				setFlowStep('done');
			} else {
				const nextTitle = currentForm.title ?? 'Formulário';
				setCompletedFormTitle(nextTitle);
				setCurrentFormIndex(0);
				setCurrentQuestionStep(0);
				setAnswers({});
				setFlowStep('betweenForms');
				saveCompanyFormularioDraft({
					version: 1,
					companyId,
					flowStep: 'betweenForms',
					allForms: remaining,
					currentFormIndex: 0,
					currentQuestionStep: 0,
					answers: {},
					completedFormTitle: nextTitle,
					totalFormsInSession,
					savedAt: Date.now(),
				});
			}
		} catch {
			/* toast no mutation */
		} finally {
			submitInFlightRef.current = false;
			setIsSubmitLocked(false);
		}
	};

	const handleBack = () => {
		if (currentQuestionStep > 0) setCurrentQuestionStep((s) => s - 1);
	};

	const handleContinueToNextForm = () => {
		setAnswers({});
		setCurrentQuestionStep(0);
		setIntroDismissed(true);
		setFlowStep('form');
		saveCompanyFormularioDraft({
			version: 1,
			companyId,
			flowStep: 'form',
			allForms,
			currentFormIndex,
			currentQuestionStep: 0,
			answers: {},
			completedFormTitle,
			totalFormsInSession,
			savedAt: Date.now(),
		});
	};

	// --- effects ---

	useEffect(() => {
		if (flowStep === 'intro' && introDismissed) {
			setFlowStep('form');
		}
	}, [flowStep, introDismissed]);

	useEffect(() => {
		let cancelled = false;

		const bootstrapSession = async () => {
			setSessionBoot('loading');
			let redirectedToOwnCompany = false;
			try {
				const me = await EmployeeService.getMe();
				if (cancelled) return;

				if (me.company_id !== companyId) {
					redirectedToOwnCompany = true;
					redirectToEmployeeCompanyUrl(me.company_id);
					return;
				}
				setPortalCompany(portalCompanyFromEmployeeMe(me));

				const draft = loadCompanyFormularioDraft(companyId);
				const answeredCount = countAnsweredKeys(draft?.answers);
				const canRestoreDraft =
					!!draft &&
					(answeredCount >= 1 || draft.flowStep === 'betweenForms');

				if (!canRestoreDraft) {
					const resCheck = await FormService.getAvailableForEmployee({
						page: 1,
						limit: 100,
					});
					if (cancelled) return;
					const availableCheck = filterEmployeeAvailableForms(
						resCheck.data ?? [],
					);
					if (availableCheck.length === 0) {
						setFlowStep('no-form');
					} else {
						setFlowStep('cpf');
					}
					return;
				}

				const res = await FormService.getAvailableForEmployee({
					page: 1,
					limit: 100,
				});
				if (cancelled) return;

				const available = filterEmployeeAvailableForms(res.data ?? []);
				if (available.length === 0) {
					clearCompanyFormularioDraft(companyId);
					setFlowStep('no-form');
					return;
				}

				const safeFormIndex = Math.min(
					draft.currentFormIndex,
					Math.max(available.length - 1, 0),
				);
				const restoredForm = available[safeFormIndex];
				const restoredActive = getActiveQuestions(
					restoredForm?.form_question ?? [],
					draft.answers,
				);
				const safeStep = Math.min(
					draft.currentQuestionStep,
					Math.max(restoredActive.length - 1, 0),
				);

				setAllForms(available);
				setTotalFormsInSession(
					(draft.totalFormsInSession ?? 0) > 0
						? draft.totalFormsInSession!
						: available.length,
				);
				setCurrentFormIndex(safeFormIndex);
				setAnswers(
					pruneInactiveQuestionAnswers(
						restoredForm?.form_question ?? [],
						draft.answers,
					),
				);
				setCurrentQuestionStep(safeStep);
				setCompletedFormTitle(draft.completedFormTitle);

				const restoredIntroDismissed =
					draft.flowStep === 'form' ||
					draft.flowStep === 'betweenForms' ||
					countAnsweredKeys(draft.answers) >= 1;

				const restoredStep: FlowStep =
					draft.flowStep === 'betweenForms' && available.length > 0
						? 'betweenForms'
						: draft.flowStep === 'form' && restoredActive.length > 0
						  ? 'form'
						  : draft.flowStep === 'intro' &&
						      !restoredIntroDismissed
						    ? 'intro'
						    : restoredActive.length > 0
						      ? 'form'
						      : 'intro';

				setIntroDismissed(restoredIntroDismissed);
				setFlowStep(restoredStep);
			} catch {
				if (!cancelled) {
					clearCompanyFormularioDraft(companyId);
					setFlowStep('cpf');
				}
			} finally {
				if (!cancelled && !redirectedToOwnCompany)
					setSessionBoot('ready');
			}
		};

		void bootstrapSession();
		return () => {
			cancelled = true;
		};
	}, [companyId, navigate]);

	useEffect(() => {
		if (sessionBoot !== 'ready') return;
		let cancelled = false;

		const watchCompanyUrl = async () => {
			try {
				const me = await EmployeeService.getMe();
				if (cancelled) return;
				if (me.company_id !== companyId) {
					redirectToEmployeeCompanyUrl(me.company_id);
				}
			} catch {
				/* sem sessão employee */
			}
		};

		void watchCompanyUrl();
		return () => {
			cancelled = true;
		};
	}, [companyId, sessionBoot, navigate]);

	useEffect(() => {
		if (sessionBoot !== 'ready' || flowStep !== 'form') return;
		if (sortedForms.length === 0) {
			setFlowStep('no-form');
			return;
		}
		if (!currentForm) {
			setCurrentFormIndex(0);
			setCurrentQuestionStep(0);
			setAnswers({});
			setFlowStep(sortedForms.length > 0 ? 'intro' : 'no-form');
		}
	}, [sessionBoot, flowStep, currentForm, sortedForms.length]);

	useEffect(() => {
		if (sessionBoot !== 'ready') return;
		if (countAnsweredKeys(answers) < 1) return;
		if (flowStep === 'cpf' || flowStep === 'done') return;

		saveCompanyFormularioDraft({
			version: 1,
			companyId,
			flowStep,
			allForms,
			currentFormIndex,
			currentQuestionStep,
			answers,
			completedFormTitle,
			totalFormsInSession,
			savedAt: Date.now(),
		});
	}, [
		sessionBoot,
		companyId,
		flowStep,
		allForms,
		currentFormIndex,
		currentQuestionStep,
		answers,
		completedFormTitle,
	]);

	useEffect(() => {
		if (totalSteps === 0) return;
		if (currentQuestionStep >= totalSteps) {
			setCurrentQuestionStep(Math.max(totalSteps - 1, 0));
		}
	}, [totalSteps, currentQuestionStep]);

	useEffect(() => {
		if (canAdvance && !prevCanAdvanceRef.current) {
			nextButtonRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
			});
		}
		prevCanAdvanceRef.current = canAdvance;
	}, [canAdvance]);

	useEffect(() => {
		scrollToTop('smooth');
	}, [currentQuestionStep]);

	useEffect(() => {
		scrollToTop('auto');
	}, [flowStep]);

	const betweenFormsCompletedCount = useMemo(() => {
		if (totalFormsInSession > 0) {
			return Math.max(1, totalFormsInSession - sortedForms.length);
		}
		return Math.max(1, sortedForms.length > 0 ? 1 : 0);
	}, [totalFormsInSession, sortedForms.length]);

	const betweenFormsTotalCount = useMemo(
		() =>
			totalFormsInSession > 0
				? totalFormsInSession
				: sortedForms.length + 1,
		[totalFormsInSession, sortedForms.length],
	);

	useEffect(() => {
		if (flowStep !== 'betweenForms') return;
		setBetweenFormsCountdown(5);
		const timer = window.setTimeout(() => {
			handleContinueToNextForm();
		}, 5000);
		const tick = window.setInterval(() => {
			setBetweenFormsCountdown((s) => (s > 0 ? s - 1 : 0));
		}, 1000);
		return () => {
			window.clearTimeout(timer);
			window.clearInterval(tick);
		};
	}, [flowStep]);

	useEffect(() => {
		if (flowStep !== 'betweenForms') return;
		const iconEl = betweenFormsIconRef.current;
		if (!iconEl) return;
		iconEl.animate(
			[
				{
					opacity: 0,
					transform: 'translateY(16px) scale(0.68) rotate(-10deg)',
				},
				{
					opacity: 1,
					transform: 'translateY(-5px) scale(1.1) rotate(4deg)',
					offset: 0.45,
				},
				{
					opacity: 1,
					transform: 'translateY(1px) scale(0.97) rotate(-2deg)',
					offset: 0.78,
				},
				{
					opacity: 1,
					transform: 'translateY(0px) scale(1) rotate(0deg)',
				},
			],
			{
				delay: 260,
				duration: 1700,
				fill: 'both',
				easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
			},
		);
	}, [flowStep]);

	// keyboard: number keys → select option
	useEffect(() => {
		if (flowStep !== 'form' || !introDismissed || !currentQuestion) return;
		if (
			currentQuestion.type === 'FREE_TEXT' ||
			isSubmitting ||
			isSubmitLocked
		)
			return;
		const options = currentQuestion.options ?? [];
		if (options.length === 0) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.defaultPrevented ||
				event.ctrlKey ||
				event.metaKey ||
				event.altKey
			)
				return;
			const target = event.target as HTMLElement | null;
			if (
				target &&
				(target.tagName === 'INPUT' ||
					target.tagName === 'TEXTAREA' ||
					target.tagName === 'SELECT' ||
					target.isContentEditable)
			)
				return;

			const num = Number.parseInt(event.key, 10);
			if (!Number.isInteger(num) || num < 1 || num > 9) return;
			const opt = options[num - 1];
			if (!opt) return;
			event.preventDefault();
			if (currentQuestion.type === 'SINGLE_CHOICE')
				handleSingleChoice(opt.key);
			else if (currentQuestion.type === 'MULTIPLE_CHOICE')
				handleMultipleToggle(opt.key);
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [
		flowStep,
		introDismissed,
		currentQuestion,
		isSubmitting,
		isSubmitLocked,
		answers,
	]);

	// keyboard: Enter → advance
	useEffect(() => {
		if (flowStep !== 'form' || !introDismissed || !currentQuestion) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key !== 'Enter' &&
				event.code !== 'Enter' &&
				event.code !== 'NumpadEnter'
			)
				return;
			if (
				!canAdvance ||
				isSubmitting ||
				isSubmitLocked ||
				submitInFlightRef.current
			)
				return;
			event.preventDefault();
			handleNext();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [
		flowStep,
		introDismissed,
		currentQuestion,
		canAdvance,
		isSubmitting,
		isSubmitLocked,
		answers,
	]);

	// keyboard: Enter on intro → start
	useEffect(() => {
		if (flowStep !== 'intro') return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === 'Enter' ||
				event.code === 'Enter' ||
				event.code === 'NumpadEnter'
			) {
				event.preventDefault();
				handleStartForm();
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [flowStep]);

	// keyboard: Enter on betweenForms → continue
	useEffect(() => {
		if (flowStep !== 'betweenForms') return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === 'Enter' ||
				event.code === 'Enter' ||
				event.code === 'NumpadEnter'
			) {
				event.preventDefault();
				handleContinueToNextForm();
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [flowStep]);

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	if (sessionBoot === 'loading') {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-[#f8f9fa] font-sans">
				<Helmet>
					<title>Formulário | Plumaa</title>
				</Helmet>
				<p className="text-sm font-medium text-stone-500">
					Carregando...
				</p>
			</div>
		);
	}

	// ==============================================================
	//  RENDER
	// ==============================================================

	const wrapWithHeader = (children: React.ReactNode) => (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Formulário | Plumaa</title>
			</Helmet>
			<Header
				showAudienceSwitcher={false}
				logoHomePath="/"
				trailing={
					<CompanyFormularioHeaderCompanyBanner
						company={portalCompany}
					/>
				}
			/>
			{children}
		</div>
	);

	// --- CPF ---
	if (flowStep === 'cpf') {
		return wrapWithHeader(
			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-6 rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
					<div className="space-y-1 text-center">
						<h1 className="text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
							Pesquisa de bem-estar
						</h1>
						<p className="text-xs font-medium text-stone-400 md:text-sm">
							Informe seu CPF para acessar o formulário
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmitCpf)}
						className="space-y-5"
					>
						<div className="space-y-1.5">
							<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
								CPF
							</label>
							<InputWrapperAuth
								className="max-w-none"
								icon={<UserRound className={iconCommonClass} />}
								error={errors.cpf?.message}
								value={watch('cpf')}
								displayError
							>
								<Input
									{...register('cpf')}
									placeholder="000.000.000-00"
									maxLength={14}
									onChange={(e) =>
										setValue(
											'cpf',
											maskCPF(e.target.value),
											{
												shouldValidate: false,
											},
										)
									}
									className="h-10 border-stone-200 text-sm focus:border-stone-400"
								/>
							</InputWrapperAuth>
						</div>

						<Button
							type="submit"
							disabled={
								isCpfStepLoading || isLoginEmployeePending
							}
							className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] py-4 text-sm font-bold text-white transition-all hover:bg-stone-800"
							text={
								isCpfStepLoading || isLoginEmployeePending
									? 'Validando...'
									: 'Continuar'
							}
							endIcon={
								<ArrowRightCircle size={16} className="ml-1" />
							}
						/>
					</form>
				</div>
			</main>,
		);
	}

	// --- No form ---
	if (flowStep === 'no-form') {
		return wrapWithHeader(
			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[480px] flex-col items-center gap-6 rounded-[2rem] bg-white p-6 text-center shadow-sm md:p-10">
					<div className="flex h-20 w-20 items-center justify-center rounded-full bg-stone-100">
						<ClipboardList
							className="h-10 w-10 text-stone-400"
							strokeWidth={1.5}
						/>
					</div>
					<div className="space-y-2">
						<h1 className="text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
							Nenhum formulário disponível
						</h1>
						<p className="max-w-sm text-sm font-medium text-stone-400">
							Não há pesquisas pendentes para o CPF informado no
							momento. Caso acredite que isso seja um erro, entre
							em contato com o RH da sua empresa.
						</p>
					</div>
					<Button
						type="button"
						onClick={() => void resetSessionToCpf()}
						className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] py-4 text-sm font-bold text-white transition-all hover:bg-stone-800"
						text="Sair"
					/>
				</div>
			</main>,
		);
	}

	// --- Intro (só antes do 1º formulário da sessão) ---
	if (flowStep === 'intro' && !introDismissed) {
		return wrapWithHeader(
			<main className="flex flex-1 flex-col md:flex-row">
				<div className="flex flex-1 flex-col justify-between bg-brand-orange/90 px-6 py-8 md:min-h-0 md:px-10 md:py-12 lg:px-14">
					<div className="flex flex-1 flex-col">
						<h1 className="max-w-xl text-3xl font-black leading-[1.1] tracking-tight text-white md:text-4xl lg:text-[2.75rem]">
							Sua voz transforma o{' '}
							<span className="italic text-white">amanhã.</span>
						</h1>
						<p className="mt-5 max-w-lg text-base leading-relaxed text-white/90 md:text-lg">
							Estamos aqui para ouvir você. Suas respostas são o
							primeiro passo para construirmos um ambiente mais
							acolhedor e eficiente.
						</p>

						{sortedForms.length > 0 && (
							<div className="mt-8 rounded-2xl border border-white/60 bg-white/95 p-4 shadow-lg md:p-5">
								<p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-orange/90">
									Formulários disponíveis
								</p>
								<ul className="mt-4 space-y-1">
									{sortedForms.map((form, index) => {
										const label =
											form.title ||
											'Formulário sem título';
										const num = String(index + 1).padStart(
											2,
											'0',
										);
										const isActive = index === 0;
										return (
											<li
												key={form.id}
												className={`flex items-center gap-3 rounded-xl px-2 py-2 ${
													isActive
														? 'border border-brand-orange bg-white px-3 py-3 shadow-sm'
														: ''
												}`}
											>
												<span
													className={`flex h-9 min-w-9 shrink-0 items-center justify-center rounded-full text-xs font-black tabular-nums ${
														isActive
															? 'bg-brand-orange/10 text-brand-orange'
															: 'bg-zinc-100 text-zinc-600'
													}`}
												>
													{num}
												</span>
												<span
													className={`min-w-0 flex-1 text-sm font-semibold leading-snug md:text-base ${
														isActive
															? 'text-zinc-900'
															: 'text-zinc-700'
													}`}
												>
													{label}
												</span>
												{isActive && (
													<span className="inline-flex size-7 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
														<ArrowRight className="size-3.5" />
													</span>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						)}
					</div>

					<div className="mt-10 flex flex-wrap items-end gap-8 border-t border-white/15 pt-8 md:mt-12 md:gap-14 md:pt-10">
						<div className="flex items-start gap-3">
							<span className="mt-0.5 inline-flex rounded-lg bg-white/15 p-2 text-white">
								<Clock className="h-5 w-5" strokeWidth={2} />
							</span>
							<div>
								<p className="text-2xl font-black tabular-nums text-white md:text-3xl">
									~{estimatedTimeLabel}
								</p>
								<p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/75">
									Tempo estimado
								</p>
							</div>
						</div>
						<div className="flex items-start gap-3">
							<span className="mt-0.5 inline-flex rounded-lg bg-white/15 p-2 text-white">
								<ShieldCheck
									className="h-5 w-5"
									strokeWidth={2}
								/>
							</span>
							<div>
								<p className="text-2xl font-black text-white md:text-3xl">
									100%
								</p>
								<p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/75">
									Confidencial
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-1 flex-col justify-center bg-white px-6 py-10 md:px-12 md:py-14 lg:px-16">
					<div className="mx-auto w-full max-w-3xl">
						<div className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-orange/90 text-white shadow-sm ring-1 ring-brand-orange/20">
							<ClipboardList
								className="h-7 w-7"
								strokeWidth={2}
							/>
						</div>
						<h2 className="mt-6 text-xl font-bold tracking-tight text-brand-orange/90 md:text-2xl">
							Antes de começar…
						</h2>
						<ol className="mt-6 list-decimal space-y-4 pl-5 text-sm leading-relaxed text-zinc-600">
							<li>
								Responda com sinceridade. Não existem respostas
								certas ou erradas, apenas a sua percepção real.
							</li>
							<li>
								Seus dados são protegidos por criptografia de
								ponta a ponta e tratados de forma anônima pelo
								RH.
							</li>
							<li>
								Ao final, você poderá agendar uma conversa de
								acompanhamento se desejar.
							</li>
						</ol>
						<div className="mt-10">
							<Button
								onClick={handleStartForm}
								text="Iniciar Questionário"
								endIcon={<ArrowRight className="h-5 w-5" />}
								className="w-full justify-center rounded-xl bg-brand-orange/90 px-6 py-3.5 font-semibold text-white hover:bg-brand-orange/80 md:w-auto md:min-w-[240px]"
							/>
						</div>
					</div>
				</div>
			</main>,
		);
	}

	// --- Between forms ---
	if (flowStep === 'betweenForms') {
		return wrapWithHeader(
			<main
				ref={stageRootRef}
				className="flex flex-1 items-center justify-center px-6 py-16"
			>
				<div className="w-full max-w-xl rounded-3xl border border-brand-orange/20 bg-white p-7 text-center shadow-sm md:p-10">
					<div
						ref={betweenFormsIconRef}
						className="mx-auto flex h-28 w-28 items-center justify-center"
					>
						<BetweenFormsProgressRing
							completed={betweenFormsCompletedCount}
							total={betweenFormsTotalCount}
						/>
					</div>
					<p className="mt-6 text-sm font-bold uppercase tracking-wider text-brand-orange">
						{betweenFormsCompletedCount}/{betweenFormsTotalCount}{' '}
						formulários
					</p>
					<h2 className="mt-4 text-2xl font-black tracking-tight text-zinc-900 md:text-3xl">
						Estamos salvando suas respostas…
					</h2>
					<p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-zinc-500">
						{completedFormTitle ? (
							<>
								Você concluiu o formulário{' '}
								<span className="font-semibold text-zinc-700">
									&ldquo;{completedFormTitle}&rdquo;
								</span>
								. Em instantes você segue para o próximo.
							</>
						) : (
							'Em instantes você seguirá para o próximo formulário.'
						)}
					</p>
					<div className="mt-8">
						<Button
							onClick={handleContinueToNextForm}
							text="Continuar para o próximo"
							endIcon={<ArrowRight className="h-5 w-5" />}
							className="w-full justify-center rounded-xl bg-brand-orange px-6 py-3.5 font-semibold text-white hover:bg-brand-orange/90"
						/>
					</div>
					<p className="mt-5 text-sm font-semibold text-zinc-400">
						{betweenFormsCountdown > 0
							? `Continuando automaticamente em ${betweenFormsCountdown}s…`
							: 'Continuando…'}
					</p>
				</div>
			</main>,
		);
	}

	// --- Done ---
	if (flowStep === 'done') {
		return wrapWithHeader(
			<main
				ref={stageRootRef}
				className="flex flex-1 items-center justify-center px-6 py-16"
			>
				<div className="w-full max-w-xl rounded-3xl border border-emerald-200 bg-white p-7 text-center shadow-sm md:p-10">
					<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
						<CheckCircle2
							className="h-10 w-10 text-emerald-500"
							strokeWidth={2}
						/>
					</div>
					<h2 className="mt-6 text-3xl font-black tracking-tight text-zinc-900 md:text-4xl">
						Tudo certo!
					</h2>
					<p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-zinc-500">
						Suas respostas foram enviadas com sucesso. Obrigado por
						participar da pesquisa de bem-estar. Seus dados são
						tratados de forma anônima e confidencial.
					</p>
					<div className="mt-8">
						<Button
							onClick={() => void resetSessionToCpf()}
							text="Sair"
							className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] px-6 py-3.5 font-semibold text-white hover:bg-stone-800"
						/>
					</div>
				</div>
			</main>,
		);
	}

	// --- Form (one question at a time) ---
	if (!currentForm || totalSteps === 0) {
		if (flowStep === 'form') {
			return wrapWithHeader(
				<main className="flex flex-1 items-center justify-center px-4 py-16">
					<p className="text-center text-sm text-stone-600">
						Nenhuma pergunta disponível neste formulário com as
						respostas atuais. Atualize a página ou fale com o RH.
					</p>
				</main>,
			);
		}
		return null;
	}

	return wrapWithHeader(
		<main
			ref={stageRootRef}
			className="flex flex-1 justify-center overflow-y-auto"
		>
			<div className="flex w-full max-w-5xl flex-col px-4 py-8 sm:px-6 sm:py-10 md:px-12 lg:px-16 lg:py-14 xl:max-w-6xl xl:px-20">
				<Helmet>
					<title>
						{currentForm.title ?? 'Questionário'} | Plumaa
					</title>
				</Helmet>

				{/* Progress bar */}
				<div className="mb-6 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 sm:gap-4">
					<p className="text-sm font-semibold tabular-nums text-zinc-800">
						PASSO {currentQuestionStep + 1} DE {totalSteps}
					</p>
					<div className="min-w-0 overflow-hidden rounded-full bg-zinc-200">
						<div
							className={`h-2 rounded-full transition-all ease-out ${PROGRESS_COLOR_CLASS}`}
							style={{
								width: `${progressPercent}%`,
								transitionDuration: '300ms',
							}}
						/>
					</div>
					<p className="text-right text-sm font-semibold tabular-nums text-zinc-800">
						{progressPercent}% COMPLETO
					</p>
				</div>

				<div className="flex flex-1 flex-col">
					{/* Badge + title */}
					<div className="mb-2 flex items-center gap-2">
						<span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
							Formulário da sua empresa
						</span>
						{currentForm.title && (
							<span className="truncate text-xs font-medium text-zinc-500">
								{currentForm.title}
							</span>
						)}
					</div>

					{/* Question number + title */}
					<div className="mt-1 flex items-center gap-3">
						<span
							className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${questionTextColorClass}`}
							style={{ backgroundColor: resolvedQuestionColor }}
						>
							{questionNumber}
						</span>
						<div className="flex min-w-0 items-center gap-2">
							{currentQuestion.icon_identifier && (
								<DynamicIcon
									name={
										currentQuestion.icon_identifier as IconName
									}
									size={26}
									className="shrink-0 text-zinc-500"
								/>
							)}
							<h1 className="min-w-0 text-2xl font-bold leading-tight text-zinc-900 md:text-3xl">
								{currentQuestion.title}
							</h1>
						</div>
					</div>

					{currentQuestion.description && (
						<p className="mt-3 text-base text-zinc-700">
							{currentQuestion.description}
						</p>
					)}

					{currentQuestion.type !== 'FREE_TEXT' && (
						<div className="mt-3 inline-flex w-fit rounded-full bg-zinc-100 px-2 py-0.5">
							<span className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
								{currentQuestion.type === 'SINGLE_CHOICE'
									? 'Escolha única'
									: 'Múltipla escolha'}
							</span>
						</div>
					)}

					{/* Options */}
					<div className="mt-8 space-y-4 sm:mt-10 sm:space-y-5 md:mt-12 md:space-y-6">
						{currentQuestion.type === 'FREE_TEXT' && (
							<textarea
								value={
									answers[String(currentQuestion.number)] ??
									''
								}
								onChange={(e) =>
									setAnswer(
										String(currentQuestion.number),
										e.target.value,
									)
								}
								placeholder="Digite sua resposta..."
								rows={4}
								className="w-full rounded-xl border border-zinc-200 bg-white p-4 text-zinc-800 shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
							/>
						)}

						{currentQuestion.type === 'SINGLE_CHOICE' &&
							(currentQuestion.options ?? []).map((opt) => {
								const selected =
									(answers[String(currentQuestion.number)] ??
										'') === opt.key;
								const rawOptColor =
									(opt.color && opt.color.trim()) ||
									'#e5e7eb';
								const resolvedOptColor =
									resolveColorInputToHex(rawOptColor) ??
									'#e5e7eb';
								const textColorCls =
									getContrastTextClassForColorInput(
										rawOptColor,
										'#e5e7eb',
									);
								const borderStyle = selected
									? { borderColor: resolvedOptColor }
									: undefined;
								const isHovered = hoveredKey === opt.key;
								const hoverBg = isHovered
									? hexToRgba(resolvedOptColor, 0.04)
									: undefined;
								const selectedBg = selected
									? hexToRgba(resolvedOptColor, 0.08)
									: undefined;

								return (
									<button
										key={opt.key}
										type="button"
										onClick={() =>
											handleSingleChoice(opt.key)
										}
										onMouseEnter={() =>
											setHoveredKey(opt.key)
										}
										onMouseLeave={() => setHoveredKey(null)}
										className="flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-white p-4 text-left shadow-sm transition-all focus:outline-none sm:gap-4 sm:p-5 md:p-6"
										style={{
											...borderStyle,
											...(hoverBg && !selected
												? { backgroundColor: hoverBg }
												: {}),
											...(selectedBg
												? {
														backgroundColor:
															selectedBg,
												  }
												: {}),
										}}
									>
										<span
											className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${textColorCls} sm:h-9 sm:w-9 md:h-10 md:w-10`}
											style={{
												backgroundColor:
													resolvedOptColor,
											}}
										>
											{selected ? (
												'✓'
											) : opt.icon_identifier ? (
												<DynamicIcon
													name={
														opt.icon_identifier as IconName
													}
													size={18}
													className="text-current"
												/>
											) : (
												opt.key
											)}
										</span>
										<span className="text-sm font-medium text-zinc-800 sm:text-base">
											{opt.label}
										</span>
									</button>
								);
							})}

						{currentQuestion.type === 'MULTIPLE_CHOICE' &&
							(currentQuestion.options ?? []).map((opt) => {
								const current = (
									answers[String(currentQuestion.number)] ??
									''
								)
									.split('')
									.map((s) => s.trim())
									.filter(Boolean);
								const selected = current.includes(opt.key);
								const rawOptColor =
									(opt.color && opt.color.trim()) ||
									'#e5e7eb';
								const resolvedOptColor =
									resolveColorInputToHex(rawOptColor) ??
									'#e5e7eb';
								const textColorCls =
									getContrastTextClassForColorInput(
										rawOptColor,
										'#e5e7eb',
									);
								const borderStyle = selected
									? { borderColor: resolvedOptColor }
									: undefined;
								const isHovered = hoveredKey === opt.key;
								const hoverBg = isHovered
									? hexToRgba(resolvedOptColor, 0.04)
									: undefined;
								const selectedBg = selected
									? hexToRgba(resolvedOptColor, 0.08)
									: undefined;

								return (
									<button
										key={opt.key}
										type="button"
										onClick={() =>
											handleMultipleToggle(opt.key)
										}
										onMouseEnter={() =>
											setHoveredKey(opt.key)
										}
										onMouseLeave={() => setHoveredKey(null)}
										className="flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-white p-4 text-left shadow-sm transition-all focus:outline-none sm:gap-4 sm:p-5 md:p-6"
										style={{
											...borderStyle,
											...(hoverBg && !selected
												? { backgroundColor: hoverBg }
												: {}),
											...(selectedBg
												? {
														backgroundColor:
															selectedBg,
												  }
												: {}),
										}}
									>
										<span
											className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${textColorCls} sm:h-9 sm:w-9 md:h-10 md:w-10`}
											style={{
												backgroundColor:
													resolvedOptColor,
											}}
										>
											{selected ? (
												'✓'
											) : opt.icon_identifier ? (
												<DynamicIcon
													name={
														opt.icon_identifier as IconName
													}
													size={18}
													className="text-current"
												/>
											) : (
												opt.key
											)}
										</span>
										<span className="text-sm font-medium text-zinc-800 sm:text-base">
											{opt.label}
										</span>
									</button>
								);
							})}
					</div>

					{/* Navigation buttons */}
					<div
						ref={nextButtonRef}
						className="mt-8 space-y-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] sm:mt-10 sm:space-y-0 sm:pb-0"
					>
						{/* Mobile layout */}
						<div className="flex items-center justify-between sm:hidden">
							<div className="min-w-0">
								{currentQuestionStep > 0 && (
									<button
										type="button"
										onClick={handleBack}
										className="flex items-center gap-1.5 rounded border-none bg-transparent text-zinc-400 transition-colors hover:text-zinc-600"
									>
										<ChevronLeft className="size-5" />
										<span className="text-sm font-semibold uppercase tracking-wide">
											Voltar
										</span>
									</button>
								)}
							</div>
						</div>

						<Button
							text={
								isSubmitting
									? 'Enviando...'
									: currentQuestionStep < totalSteps - 1
									  ? 'Próximo'
									  : 'Enviar respostas'
							}
							disabled={
								!canAdvance || isSubmitting || isSubmitLocked
							}
							style={{
								backgroundColor: resolvedQuestionColor,
								borderColor: resolvedQuestionColor,
							}}
							onClick={() => void handleNext()}
							className={`mb-2 w-full justify-center rounded-full px-6 py-2.5 font-bold shadow-md hover:brightness-105 focus:outline-none disabled:opacity-50 sm:hidden ${questionTextColorClass}`}
						/>

						{/* Desktop layout */}
						<div className="hidden items-center justify-between gap-4 sm:flex">
							<div className="min-w-0 shrink-0">
								{currentQuestionStep > 0 && (
									<button
										type="button"
										onClick={handleBack}
										className="flex items-center gap-1.5 rounded border-none bg-transparent text-zinc-400 transition-colors hover:text-zinc-600"
									>
										<ChevronLeft className="size-5" />
										<span className="text-sm font-semibold uppercase tracking-wide">
											Voltar
										</span>
									</button>
								)}
							</div>
							<Button
								text={
									isSubmitting
										? 'Enviando...'
										: currentQuestionStep < totalSteps - 1
										  ? 'Próximo'
										  : 'Enviar respostas'
								}
								disabled={
									!canAdvance ||
									isSubmitting ||
									isSubmitLocked
								}
								style={{
									backgroundColor: resolvedQuestionColor,
									borderColor: resolvedQuestionColor,
								}}
								onClick={() => void handleNext()}
								className={`shrink-0 rounded-full px-6 py-2.5 font-bold shadow-md hover:brightness-105 focus:outline-none disabled:opacity-50 ${questionTextColorClass}`}
							/>
						</div>
					</div>
				</div>
			</div>
		</main>,
	);
}

const CompanyFormulario = () => {
	const { companyId: companyIdParam } = useParams<{ companyId: string }>();
	const companyId = parseCompanyFormularioCompanyId(companyIdParam);
	if (!companyId) {
		return <NotFound />;
	}
	return <CompanyFormularioContent companyId={companyId} />;
};

export { CompanyFormulario, companyFormularioPath };
